import { useState, useEffect } from 'react';
import { Breadcrumb, DateInputField } from 'components';
import {
    Typography,
    Grid,
    TextField,
    Button,
    Tooltip,
    Box,
    Snackbar,
    Alert,
    IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { getAnalyticsReports, getActiveClients } from 'services';
import useFetch from '../../../customHooks/useFetch';
import { ClientNameDropdown } from '../../custom_component/clientNameDropdown';
import moment from 'moment';
import ClientSearch from './Components/ClientSearch';
import DownloadIcon from '@mui/icons-material/Download';

interface FilterState {
    batchId: string;
    dateSampledFrom: string;
    dateSampledTill: string;
    projectName: string;
    clientReference: string;
    eurofinsLabNo: string;
    dateReceivedFrom: string;
    dateReceivedTill: string;
}

export const Analytics = () => {
    const [filters, setFilters] = useState<FilterState>({
        batchId: '',
        dateSampledFrom: '',
        dateSampledTill: '',
        projectName: '',
        clientReference: '',
        eurofinsLabNo: '',
        dateReceivedFrom: '',
        dateReceivedTill: ''
    });
    const [userInfo] = useFetch();
    const [paginationModel, setPaginationModel] = useState({
        page: 1,
        pageSize: 10,
        total: 0,
        data: [],
        isLoading: false
    });
    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedInternalClientId, setSelectedInternalClientId] = useState('');
    const [activeClientList, setActiveClientList] = useState([]);
    const [showSearchMsg, setShowSearchMsg] = useState(true);
    const [notificationOpen, setNotificationOpen] = useState(false);
    const [successNotification, setSuccessNotification] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [dateSampledError, setDateSampledError] = useState('');
    const [sampleReceivedError, setSampleReceivedError] = useState('');
    const jwt: any = localStorage.getItem('token');
    const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;

    useEffect(() => {
        if (userInfo?.userMap !== null) {
            setSelectedClientId(userInfo?.userMap[0]?.clientID);
        }
    }, [userInfo]);

    useEffect(() => {
        if (labUserCheck?.UserType !== 'External') {
            getActiveClients().then((res: any) => {
                if (res?.status === 200) {
                    setActiveClientList(res.data);
                }
            })
        }
    }, [userInfo]);

    let nav: any = [{ name: 'Home', path: '/home' }, { name: 'Report/Sample Data', path: '/home/analytics' }];

    const handleInputChange = (field: keyof FilterState, value: string) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSearch = async (searchFilters?: FilterState) => {
        if (!selectedInternalClientId && labUserCheck?.UserType !== 'External') {
            setWarningMessage('Please select a client name before proceeding with search.');
            setNotificationOpen(true);
            return;
        }
        if (!selectedClientId && labUserCheck?.UserType === 'External') {
            setWarningMessage('Please select a client name before proceeding with search.');
            setNotificationOpen(true);
            return;
        }
        setShowSearchMsg(false);
        setPaginationModel(old => ({ ...old, isLoading: true }));
        const usedFilters = searchFilters ?? filters;
        const clientId = labUserCheck?.UserType !== 'External' ? selectedInternalClientId : selectedClientId;
        try {
            const response = await getAnalyticsReports(
                clientId,
                usedFilters?.batchId,
                usedFilters?.dateSampledFrom,
                usedFilters?.dateSampledTill,
                usedFilters?.projectName,
                usedFilters?.clientReference,
                usedFilters?.eurofinsLabNo,
                usedFilters?.dateReceivedFrom,
                usedFilters?.dateReceivedTill
            );
            if (response.status === 200 && Array.isArray(response.data)) {
                const mappedData = response.data.map((row: any, index: number) => ({
                    ...row,
                    id: index,
                    batchId: row.id
                }));
                if (mappedData.length > 0) {
                    setPaginationModel(old => ({
                        ...old,
                        isLoading: false,
                        data: mappedData,
                        total: mappedData.length,
                        page: 1
                    }));
                } else {
                    setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0, page: 1 }));
                    setWarningMessage('No data found for the selected criteria.');
                    setNotificationOpen(true);
                }
            } else {
                setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0, page: 1 }));
                setWarningMessage('No data found for the selected criteria.');
                setNotificationOpen(true);
            }
        } catch (error: any) {
            setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0, page: 1 }));
            setWarningMessage(error?.message || 'Error occurred while fetching data.');
            setNotificationOpen(true);
        }
    };

    const handleDownloadApi = async (searchFilters?: FilterState) => {
        const usedFilters = searchFilters ?? filters;
        const clientId = labUserCheck?.UserType !== 'External' ? selectedInternalClientId : selectedClientId;

        const token = localStorage.getItem('token');
        const params: Record<string, string> = {};
        Object.entries(usedFilters).forEach(([key, value]) => {
            if (value && value !== '') {
                params[key] = value;
            }
        });

        const queryString = new URLSearchParams(params).toString();
        const url = `${process.env.REACT_APP_BASE_URL}/BatchResults/DownloadSampleDataReport/${clientId}${queryString ? `?${queryString}` : ''}`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'Authorization': token ? `Bearer ${token}` : '',
                },
            });

            if (!response.ok) {
                setWarningMessage(`${response.status} - Failed to download Excel file.`);
                setNotificationOpen(true);
                return;
            }

            let fileName = 'SampleData.xlsx';
            const now = new Date();
            const dd = String(now.getDate()).padStart(2, '0');
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            const yyyy = now.getFullYear();
            fileName = `SampleData${dd}${mm}${yyyy}.xlsx`;
            
            const blob = await response.blob();
            const excelBlob = new Blob([blob], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const downloadUrl = window.URL.createObjectURL(excelBlob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
            setSuccessMessage('Excel download started.');
            setSuccessNotification(true);
        } catch (err: any) {
            setWarningMessage(err?.message ? `Failed to download Excel file: ${err.message}` : 'Failed to download Excel file.');
            setNotificationOpen(true);
        }
    };

    const handleClear = () => {
        const clearedFilters: FilterState = {
            batchId: '',
            dateSampledFrom: '',
            dateSampledTill: '',
            projectName: '',
            clientReference: '',
            eurofinsLabNo: '',
            dateReceivedFrom: '',
            dateReceivedTill: ''
        };
        setFilters(clearedFilters);
        setPaginationModel(old => ({ ...old, isLoading: false, data: [], total: 0 }));
        setShowSearchMsg(true);
        setSampleReceivedError('');
        setDateSampledError('');
    };

    const hasFilters = Object.values(filters).some(value =>
        value !== ''
    );

    const handleDownloadReport = async (row: any) => {
        try {
            // await downloadReportApi(row.batchId);
            setSuccessMessage('Report download started.');
            setSuccessNotification(true);
        } catch (error: any) {
            setWarningMessage(error?.message || 'Failed to download report.');
            setNotificationOpen(true);
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'batchId',
            headerName: 'Report No',
            width: 120,
            renderCell: (params) => (
                <Tooltip title={params.value ?? ''}>
                    <span>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'projectName',
            headerName: 'Project Name',
            width: 200,
            renderCell: (params) => (
                <Tooltip title={params.value ?? ''}>
                    <span>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'projectID',
            headerName: 'Project ID',
            width: 150,
            renderCell: (params) => (
                <Tooltip title={params.value ?? ''}>
                    <span>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'clientReference',
            headerName: 'Client Reference',
            width: 200,
            renderCell: (params) => (
                <Tooltip title={params.value ?? ''}>
                    <span>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'eurofinsLabNo',
            headerName: 'Eurofins Lab No',
            width: 150,
            renderCell: (params) => (
                <Tooltip title={params.value ?? ''}>
                    <span>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'sampleReceived',
            headerName: 'Sample Received',
            width: 130,
            valueFormatter: (params) =>
                params.value ? moment(params.value).format('DD/MM/YYYY') : '',
            renderCell: (params) => (
                <Tooltip title={params.value ? moment(params.value).format('DD/MM/YYYY') : ''}>
                    <span>{params.value ? moment(params.value).format('DD/MM/YYYY') : ''}</span>
                </Tooltip>
            ),
        },
        {
            field: 'dateSampled',
            headerName: 'Date Sampled',
            width: 130,
            valueFormatter: (params) =>
                params.value ? moment(params.value).format('DD/MM/YYYY') : '',
            renderCell: (params) => (
                <Tooltip title={params.value ? moment(params.value).format('DD/MM/YYYY') : ''}>
                    <span>{params.value ? moment(params.value).format('DD/MM/YYYY') : ''}</span>
                </Tooltip>
            ),
        },
        {
            field: 'analysis',
            headerName: 'Analysis',
            width: 180,
            renderCell: (params) => (
                <Tooltip title={params.value ?? ''}>
                    <span>{params.value}</span>
                </Tooltip>
            ),
        }
    ];

    const hasFiltersSearch = Object.values(filters).some(value => value !== '');

    const hasErrors = !!dateSampledError || !!sampleReceivedError;

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', cursor: 'pointer' }}>
                <Breadcrumb navList={nav} />
            </div>
            <Typography variant="h6" gutterBottom align='left' mb={2}>
                Report/Sample Data
            </Typography>
            {userInfo && (
                <Box mb={2} display="flex" justifyContent="flex-start">
                    <ClientNameDropdown
                        selectedClientId={selectedClientId || ''}
                        setSelectedClientId={setSelectedClientId}
                        setPaginationModel={setPaginationModel}
                        pageName="analytics"
                    />
                </Box>
            )}
            {labUserCheck?.UserType !== 'External' && (
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={6}>
                        <Box mb={2} display="flex" justifyContent="flex-start">
                            <ClientSearch
                                data={activeClientList}
                                setSelectedInternalClientId={setSelectedInternalClientId}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} />
                </Grid>
            )}
            <form style={{ marginBottom: '16px', backgroundColor: '#f5f5f5', padding: '24px', borderRadius: '6px' }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Report No"
                            type="number"
                            value={filters.batchId}
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                handleInputChange('batchId', value);
                            }}
                            variant="outlined"
                            size="small"
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                min: 0,
                                style: {
                                    MozAppearance: 'textfield',
                                }
                            }}
                            sx={{
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                                '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Project Name / ID"
                            value={filters.projectName}
                            onChange={(e) => handleInputChange('projectName', e.target.value)}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Client Reference"
                            value={filters.clientReference}
                            onChange={(e) => handleInputChange('clientReference', e.target.value)}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Eurofins Lab No"
                            value={filters.eurofinsLabNo}
                            onChange={(e) => handleInputChange('eurofinsLabNo', e.target.value)}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DateInputField
                            fullWidth
                            label="Date Sampled (From)"
                            value={filters.dateSampledFrom}
                            onChange={(iso) => {
                                const todayStr = new Date().toISOString().split('T')[0];
                                setDateSampledError('');
                                if (iso && iso > todayStr) {
                                    setDateSampledError('Date Sampled (From) cannot be in the future.');
                                    return;
                                }
                                if (filters.dateSampledTill && iso && filters.dateSampledTill < iso) {
                                    handleInputChange('dateSampledTill', '');
                                    setDateSampledError('Date Sampled (To) cannot be earlier than Date Sampled (From).');
                                }
                                handleInputChange('dateSampledFrom', iso);
                            }}
                            size="small"
                            max={new Date().toISOString().split('T')[0]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DateInputField
                            fullWidth
                            label="Date Sampled (To)"
                            value={filters.dateSampledTill}
                            onChange={(iso) => {
                                const fromDate = filters.dateSampledFrom;
                                setDateSampledError('');
                                if (fromDate && iso) {
                                    const from = new Date(fromDate);
                                    const to = new Date(iso);
                                    if (to < from) {
                                        setDateSampledError('Date Sampled (To) cannot be earlier than Date Sampled (From).');
                                        return;
                                    }
                                    const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
                                    if (months > 6) {
                                        setDateSampledError('Date Sampled (To) should be within 6 months from Date Sampled (From).');
                                        return;
                                    }
                                }
                                handleInputChange('dateSampledTill', iso);
                            }}
                            size="small"
                            min={filters.dateSampledFrom || undefined}
                            max={new Date().toISOString().split('T')[0]}
                            error={!!dateSampledError}
                            helperText={dateSampledError}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DateInputField
                            fullWidth
                            label="Sample received (From)"
                            value={filters.dateReceivedFrom}
                            onChange={(iso) => {
                                const todayStr = new Date().toISOString().split('T')[0];
                                setSampleReceivedError('');
                                if (iso && iso > todayStr) {
                                    setSampleReceivedError('Sample received (From) cannot be in the future.');
                                    return;
                                }
                                if (filters.dateReceivedTill && iso && filters.dateReceivedTill < iso) {
                                    handleInputChange('dateReceivedTill', '');
                                    setSampleReceivedError('Sample received (To) cannot be earlier than Sample received (From).');
                                }
                                handleInputChange('dateReceivedFrom', iso);
                            }}
                            size="small"
                            max={new Date().toISOString().split('T')[0]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DateInputField
                            fullWidth
                            label="Sample received (To)"
                            value={filters.dateReceivedTill}
                            onChange={(iso) => {
                                const fromDate = filters.dateReceivedFrom;
                                setSampleReceivedError('');
                                if (fromDate && iso < fromDate) {
                                    return;
                                }
                                if (fromDate && iso) {
                                    const from = new Date(fromDate);
                                    const to = new Date(iso);
                                    const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
                                    if (months > 6) {
                                        setSampleReceivedError('Sample received (To) should be within 6 months from Sample received (From).');
                                        return;
                                    }
                                }
                                handleInputChange('dateReceivedTill', iso);
                            }}
                            size="small"
                            min={filters.dateReceivedFrom || undefined}
                            max={new Date().toISOString().split('T')[0]}
                            error={!!sampleReceivedError}
                            helperText={sampleReceivedError}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SearchIcon />}
                            onClick={() => handleSearch()}
                            disabled={paginationModel.isLoading || !hasFiltersSearch || hasErrors}
                        >
                            {paginationModel.isLoading ? 'Searching...' : 'Search'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<ClearIcon />}
                            onClick={handleClear}
                            disabled={!hasFilters}
                        >
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            </form>
            {showSearchMsg && (
                <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mt: 1, mb: 2, fontStyle: 'italic', fontWeight: 'bold' }}>
                        <span style={{ color: '#f00' }}>*</span> Please enter the criteria in the filter above and click the search button.
                    </Typography>
                </Grid>
            )}

            {paginationModel.data.length > 0 && (
                <Box mb={2} display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                            handleDownloadApi();
                        }}
                    >
                        Export As Excel
                    </Button>
                </Box>
            )}

            {/* Data Table */}
            <div style={{ width: '100%', height: 'auto', marginTop: 2 }}>
                <DataGrid
                    autoHeight
                    sx={{
                        '.MuiDataGrid-columnHeaders': {
                            backgroundColor: '#003883',
                            color: '#fff'
                        },
                        '.MuiDataGrid-menuIcon .MuiSvgIcon-root': {
                            color: '#fff'
                        },
                        '.MuiDataGrid-iconButtonContainer .MuiSvgIcon-root': {
                            color: '#fff'
                        }
                    }}
                    columns={columns}
                    rows={paginationModel.data}
                    checkboxSelection={false}
                    pageSize={10}
                    rowsPerPageOptions={[]}
                    pagination
                    loading={paginationModel.isLoading}
                />
            </div>
            <Snackbar
                open={notificationOpen}
                autoHideDuration={3000}
                onClose={() => setNotificationOpen(false)}
            >
                <Alert onClose={() => setNotificationOpen(false)} severity="warning" sx={{ width: '100%' }}>
                    <b>{warningMessage}</b>
                </Alert>
            </Snackbar>
            <Snackbar
                open={successNotification}
                autoHideDuration={3000}
                onClose={() => setSuccessNotification(false)}
            >
                <Alert onClose={() => setSuccessNotification(false)} severity="success" sx={{ width: '100%' }}>
                    <b>{successMessage}</b>
                </Alert>
            </Snackbar>
        </>
    );
}
